import React, { useEffect, useState, useCallback } from "react";
import { Search, CheckCircle } from "lucide-react";
import * as XLSX from "xlsx-js-style";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useModal } from "../../hooks/useModal";
import { Modal } from "../ui/modal";
import { DownArrow, PlusIcon, UpArrow } from "../svg/Icons";
import RowsPerPageSelector from "../common/RowsPerPageSelector";
import Pagination from "../common/Pagination";
import LogButton from "../common/LogButton";
import { PhoneCall, Calendar, CheckSquare } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash, faEye } from "@fortawesome/free-solid-svg-icons";
import { styles } from "../../styles/pages/Lead/LeadTablestyle";
import Loader from "../common/Loader";
import { ReactComponent as DownloadIcon } from "../../icons/ExcelDownloadIcon.svg";
import { Tooltip } from "../../components/common/Tooltip";

// Define API response structure
interface APIUser {
  id: number;
  name: string;
  email: string;
  contact: string;
  source: string;
  status: string;
  stage: string;
  company_name: string;
  opportunity_amount: string;
  call_count: number;
  meeting_count: number;
  task_count: number;
  log: string;
}

// Define Lead structure for frontend
interface Lead {
  id: number;
  name: string;
  email: string;
  contact: string;
  source: string;
  status: string;
  stage: string;
  company_name: string;
  opportunity_amount: string;
  call_count: number;
  meeting_count: number;
  task_count: number;
  log: string;
}

const LeadTable: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [sortColumn, setSortColumn] = useState<keyof Lead | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { isOpen, openModal, closeModal } = useModal();
  const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null);
  const [isDeleteSuccessModalOpen, setDeleteSuccessModalOpen] = useState(false);

  const getAuthToken = (): string | null => {
    const token = sessionStorage.getItem("access_token");
    if (!token) {
      toast.error("Authentication error! Please log in.");
      console.error("No auth token found.");
    }
    return token;
  };

  const openTeamsMeeting = (email: string) => {
    if (!email) {
      toast.error("No email found for this lead.");
      return;
    }

    const now = new Date();
    const start = now.toISOString();
    const end = new Date(now.getTime() + 30 * 60 * 1000).toISOString(); // +30 mins

    const subject = encodeURIComponent("CRM Meeting with " + email);
    const attendees = encodeURIComponent(`mailto:${email}`);

    const meetingLink = `https://teams.microsoft.com/l/meeting/new?subject=${subject}&attendees=${attendees}&startTime=${start}&endTime=${end}`;

    window.open(meetingLink, "_blank");
  };


  // Memoize fetchLeads to prevent unnecessary re-renders
  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setError(null);

    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/leads/list`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ search: "" }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status === 200) {
        const formattedLeads: Lead[] = Array.isArray(result.leads)
          ? result.leads.map((lead: APIUser) => ({
            id: lead.id,
            name: lead.name,
            email: lead.email,
            contact: lead.contact,
            source: lead.source,
            status: lead.status,
            stage: lead.stage,
            company_name: lead.company_name,
            opportunity_amount: lead.opportunity_amount,
            call_count: lead.call_count || 0,
            meeting_count: lead.meeting_count || 0,
            task_count: lead.task_count || 0,
            log: lead.log,
          }))
          : [];
        setLeads(formattedLeads);
      } else {
        setLeads([]);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDeleteClick = (leadId: number) => {
    setSelectedLeadId(leadId);
    openModal();
  };

  const handleConfirmDelete = async () => {
    if (!selectedLeadId) return;

    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/leads/delete`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ id: selectedLeadId }),
        }
      );

      if (response.ok) {
        // const result = await response.json();
        setDeleteSuccessModalOpen(true);
        setTimeout(() => setDeleteSuccessModalOpen(false), 2000);
        setLeads((prevLeads) => {
          const updatedLeads = prevLeads.filter(
            (lead) => lead.id !== selectedLeadId
          );
          if (updatedLeads.length > 0 && displayedLeads.length === 1) {
            setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
          }
          return updatedLeads;
        });
        closeModal();
      } else {
        const errorData = await response.json();
        console.error("Error deleting lead:", errorData);
        toast.error(errorData.message || "Failed to delete lead.");
      }
    } catch (err) {
      console.error("Error deleting lead:", err);
      toast.error("An unexpected error occurred.");
    }
  };

  // Filter and sort Leads
  const filteredLeads = leads.filter((lead) =>
    Object.values(lead)
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const sortedLeads = [...filteredLeads].sort((a, b) => {
    if (!sortColumn) return 0;
    const valA = a[sortColumn];
    const valB = b[sortColumn];
    return typeof valA === "string" && typeof valB === "string"
      ? sortOrder === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA)
      : sortOrder === "asc"
        ? (valA as number) - (valB as number)
        : (valB as number) - (valA as number);
  });

  const displayedLeads = sortedLeads.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );
  const totalPages = Math.ceil(filteredLeads.length / rowsPerPage);

  const handlePageChange = (newPage: number) => setCurrentPage(newPage);

  const handleSort = (column: keyof Lead) => {
    setSortColumn(column);
    setSortOrder(sortColumn === column && sortOrder === "asc" ? "desc" : "asc");
  };

  const handleDownload = () => {
    const dataToDownload = searchTerm
      ? filteredLeads
      : sortColumn
        ? sortedLeads
        : leads;

    if (!dataToDownload.length) {
      toast.error("No data available to download.");
      return;
    }

    const formattedData = dataToDownload.map((lead) => ({
      Name: lead.name,
      Email: lead.email,
      MobileNumber: lead.contact,
      Source: lead.source,
      Status: lead.status,
      "Call Count": lead.call_count,
      "Meeting Count": lead.meeting_count,
      "Task Count": lead.task_count,
      Log: lead.log,
    }));

    const headers = Object.keys(formattedData[0]);
    const worksheet = XLSX.utils.aoa_to_sheet([]);

    // 1. Add title in A1
    XLSX.utils.sheet_add_aoa(worksheet, [["Lead Data List"]], { origin: "A1" });

    // 2. Merge title across all columns
    worksheet["!merges"] = [
      {
        s: { r: 0, c: 0 },
        e: { r: 0, c: headers.length - 1 },
      },
    ];

    // 3. Style the title cell
    worksheet["A1"].s = {
      font: { bold: true, sz: 14 },
      alignment: { horizontal: "center", vertical: "center" },
    };

    // 4. Add headers in row 2
    XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: "A2" });

    // 5. Bold header cells
    headers.forEach((_, i) => {
      const col = String.fromCharCode(65 + i); // A, B, C...
      const cell = `${col}2`;
      if (worksheet[cell]) {
        worksheet[cell].s = {
          font: { bold: true },
        };
      }
    });

    // 6. Add data starting from row 3
    XLSX.utils.sheet_add_json(worksheet, formattedData, {
      origin: "A3",
      skipHeader: true,
    });

    // 7. Auto-size columns
    worksheet["!cols"] = headers.map((key) => ({
      wch:
        Math.max(
          key.length,
          ...formattedData.map(
            (row) =>
              (row[key as keyof typeof row] as string)?.toString().length || 0
          ),
          "Lead Data List".length
        ) + 2,
    }));

    // 8. Export to file
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");
    XLSX.writeFile(
      workbook,
      searchTerm ? `LeadData_${searchTerm}.xlsx` : "LeadData.xlsx"
    );
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>Lead List</h3>
      </div>

      <div className={styles.tableCard}>
        <div className={styles.headerControls}>
          <RowsPerPageSelector
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
          />
          <div className={styles.controlsInner}>
            <div className={styles.searchContainer}>
              <Search className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search..."
                className={styles.searchInput}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button className={styles.downloadButton} onClick={handleDownload}>
              Download
              <DownloadIcon className={styles.downloadIcon} />
            </button>
            <Link to="/lead-add" className={styles.addButtonContainer}>
              <button className={styles.addButton}>
                Add New Lead
                <PlusIcon />
              </button>
            </Link>
          </div>
        </div>
        {error && <p className={styles.errorText}>{error}</p>}

        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr className={styles.theadRow}>
                <th
                  className={styles.thName}
                  onClick={() => handleSort("name")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Name</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th
                  className={styles.thEmail}
                  onClick={() => handleSort("email")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Email</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th
                  className={styles.thMobileNumber}
                  onClick={() => handleSort("contact")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Mobile Number</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th
                  className={styles.thRole}
                  onClick={() => handleSort("source")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Source</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th
                  className={styles.thRole}
                  onClick={() => handleSort("status")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Status</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                {/* <th
                  className={styles.thRole}
                  onClick={() => handleSort("stage")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Stage</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th> */}
                {/* <th
                  className={styles.thRole}
                  onClick={() => handleSort("company_name")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Company Name</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th> */}
                {/* <th
                  className={styles.thRole}
                  onClick={() => handleSort("opportunity_amount")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Opportunity Amount</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th> */}

                <th className={styles.thAction}>
                  <p className={styles.thText}>Action</p>
                </th>
                <th className={styles.thAction}>
                  <p className={styles.thText}>Task</p>
                </th>
                <th className={styles.thLog}>
                  <p className={styles.thText}>Log</p>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr className={styles.loadingRow}>
                  <td colSpan={8} className="text-center py-4">
                    <Loader />
                  </td>
                </tr>
              ) : displayedLeads.length > 0 ? (
                displayedLeads.map((lead) => (
                  <tr key={lead.id} className={styles.tbodyRow}>
                    <td className={styles.tdName}>{lead.name}</td>
                    <td className={styles.tdEmail}>{lead.email}</td>
                    <td className={styles.tdMobileNumber}>{lead.contact}</td>
                    <td className={styles.tdRole}>{lead.source}</td>
                    <td className={styles.tdRole}>{lead.status}</td>
                    {/* <td className={styles.tdRole}>{lead.stage}</td> */}
                    {/* <td className={styles.tdRole}>{lead.company_name}</td> */}
                    {/* <td className={styles.tdRole}>
                      ₹{lead.opportunity_amount}
                    </td> */}
                    <td className={styles.tdAction}>
                      <div className={styles.actionContainer}>
                        <Tooltip content="View" bgColor="bg-green-600">
                          <Link
                            to="/lead-view"
                            state={{ lead_id: lead.id }}
                            className="inline-flex"
                          >
                            <button className={styles.viewButton}>
                              <FontAwesomeIcon
                                icon={faEye}
                                className="text-white text-sm"
                              />
                            </button>
                          </Link>
                        </Tooltip>
                        <Tooltip content="Edit" bgColor="bg-[#2f6aa7]">
                          <Link
                            to={`/lead-edit/${lead.id}`}
                            className="inline-flex"
                          >
                            <button className={styles.editButton}>
                              <FontAwesomeIcon
                                icon={faEdit}
                                className="text-white text-sm"
                              />
                            </button>
                          </Link>
                        </Tooltip>
                        <Tooltip content="Delete" bgColor="bg-red-600">
                          <button
                            className={styles.deleteButton}
                            onClick={() => handleDeleteClick(lead.id)}
                          >
                            <FontAwesomeIcon
                              icon={faTrash}
                              className="text-white text-sm"
                            />
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                    <td className={styles.tdAction}>
                      <div className={styles.actionContainer}>
                        {/* Phone Call Button */}
                        <div className="relative group">
                          <Tooltip
                            content="Call"
                            bgColor="bg-gradient-to-br from-teal-500 to-teal-700"
                          >
                            <Link to={`/call-list/${lead.id}`}>
                              <button className="w-6 h-6 bg-gradient-to-br from-teal-500 to-teal-700 hover:from-teal-600 hover:to-teal-800 flex items-center justify-center rounded-md shadow transition">
                                <PhoneCall className="w-4 h-4 text-white" />
                              </button>
                            </Link>
                          </Tooltip>
                          <span className="absolute -top-1 -right-1 bg-gradient-to-br from-teal-400 to-teal-700 text-white text-[7px] font-semibold w-3 h-3 rounded-full flex items-center justify-center shadow-sm group-hover:scale-110 group-active:scale-125 transition-transform duration-200">
                            {lead.call_count || 0}
                          </span>
                        </div>

                        {/* Meeting Button */}
                        <div className="relative group">
                          <Tooltip
                            content="Meeting"
                            bgColor="bg-gradient-to-br from-purple-500 to-purple-700"
                          >
                            <Link to={`/lead-meeting/${lead.id}`}>
                              <button className="w-6 h-6 bg-purple-600 hover:bg-purple-700 flex items-center justify-center rounded-md shadow transition">
                                <Calendar className="w-4 h-4 text-white" />
                              </button>
                            </Link>
                          </Tooltip>
                          <span className="absolute -top-1 -right-1 bg-gradient-to-br from-purple-500 to-purple-700 text-white text-[7px] font-semibold w-3 h-3 rounded-full flex items-center justify-center shadow-sm group-hover:scale-110 group-active:scale-125 transition-transform duration-200">
                            {lead.meeting_count || 0}
                          </span>
                        </div>

                        {/* Task Button */}
                        <div className="relative group">
                          <Tooltip
                            content="Task"
                            bgColor="bg-gradient-to-br from-orange-500 to-orange-700"
                          >
                            <Link to={`/lead-task/${lead.id}`}>
                              <button className="w-6 h-6 bg-orange-600 hover:bg-orange-700 flex items-center justify-center rounded-md shadow transition">
                                <CheckSquare className="w-4 h-4 text-white" />
                              </button>
                            </Link>
                          </Tooltip>
                          <span className="absolute -top-1 -right-1 bg-gradient-to-br from-orange-500 to-orange-700 text-white text-[7px] font-semibold w-3 h-3 rounded-full flex items-center justify-center shadow-sm group-hover:scale-110 group-active:scale-125 transition-transform duration-200">
                            {lead.task_count || 0}
                          </span>


                        </div>
                        <div className="relative group">
                          <Tooltip content="Schedule in Teams" bgColor="bg-[#4b70dd]">
                            <button
                              onClick={() => openTeamsMeeting(lead.email)}
                              className="w-6 h-6 bg-[#4b70dd] hover:bg-[#3b5ec4] flex items-center justify-center rounded-md shadow transition"
                            >
                              <img
                                src="https://cdn-icons-png.flaticon.com/512/906/906343.png" // Teams logo icon
                                alt="Teams"
                                className="w-4 h-4"
                              />
                            </button>
                          </Tooltip>
                        </div>
                      </div>
                    </td>
                    <td className={styles.tdLog}>
                      <div className={styles.actionContainer}>
                        <LogButton log={lead.log} />
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr className={styles.loadingRow}>
                  <td colSpan={12} className={styles.noDataText}>
                    No records match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          rowsPerPage={rowsPerPage}
          filteredLength={filteredLeads.length}
          handlePageChange={handlePageChange}
        />
      </div>

      <Modal isOpen={isOpen} onClose={closeModal} className={styles.modal}>
        <button
          onClick={closeModal}
          className={styles.modalCloseButton}
        ></button>
        <div className="flex justify-center">
          <div className={styles.modalIconContainer}>
            <FontAwesomeIcon icon={faTrash} className={styles.modalIcon} />
          </div>
        </div>
        <h4 className={styles.modalTitle}>Confirm Delete</h4>
        <p className={styles.modalText}>
          Are you sure you want to delete this lead?
        </p>
        <div className={styles.modalButtonContainer}>
          <button onClick={closeModal} className={styles.modalCancelButton}>
            Cancel
          </button>
          <button
            onClick={handleConfirmDelete}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={isDeleteSuccessModalOpen}
        onClose={() => setDeleteSuccessModalOpen(false)}
        className="w-[80%] max-w-[400px] bg-white dark:bg-gray-900 px-6 py-6 rounded-lg shadow-xl mx-auto text-center relative"
      >
        <div className="flex justify-center">
          <div className="w-14 h-14 flex items-center justify-center bg-green-100 text-green-600 rounded-full">
            <CheckCircle className="w-6 h-6" />
          </div>
        </div>
        <h4 className="text-theme-xl font-bold text-gray-800 dark:text-white mt-4">
          Lead Deleted Successfully!
        </h4>
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => setDeleteSuccessModalOpen(false)}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            OK
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default LeadTable;
